<script lang="ts" setup>
  import { useBreakpoints, useCustomerOrderAbility, useCustomerAccepted, useGoogleTagManager, selectItemEvent } from '@storefront/storefront';
  import { computed, ref } from 'vue';
  import { useShared, useHttpClient, useTranslator, useRouter } from '@othercommerce/foundation';
  import { ProductType } from '@othercommerce/domain';
  import type { ProductListingResource } from '@storefront/resources';
  import { Features } from '@shared/features';

  interface Props {
    product: ProductListingResource,
    header?: string,
    horizontal?: boolean,
    loading?: boolean,
    selectable?: boolean,
    selected?: boolean,
  }

  const props = withDefaults(defineProps<Props>(), {
    header: 'h2',
    horizontal: false,
    loading: false,
    selectable: false,
    selected: false,
  });

  const http = useHttpClient();
  const shared = useShared();
  const canCustomerOrder = useCustomerOrderAbility();
  const isCustomerAccepted = useCustomerAccepted();
  const breakpoints = useBreakpoints();
  const translator = useTranslator();
  const router = useRouter();
  const gtm = useGoogleTagManager();
  const loadingComparator = ref(false);

  const emit = defineEmits(['toggleSelected']);

  const isCompared = computed(() => {
    if (props.product.variantId && shared.value.comparator) {
      return shared.value.comparator.map((c: any) => c.variant_id).includes(props.product.variantId);
    } else {
      return false;
    }
  });

  const isComparator = computed(() => Object.keys(shared.value.comparator).length > 0);

  function toggleComparator() {
    loadingComparator.value = true;
    if (isCompared.value) {
      http.delete(router.route('web.comparator.destroy', { 'variantId': props.product.variantId })).then(() => loadingComparator.value = false);
    } else {
      http.post(router.route('web.comparator.store', { 'variantId': props.product.variantId })).then((() => loadingComparator.value = false));
    }
  }

  function onCardClick() {
    if (!props.selectable) {
      onRedirect(target.value);
      return;
    }

    emit('toggleSelected');
  }

  const compareTitle = computed(() => {
    return isCompared.value ? translator.trans('SHOP.COMPARATOR.ACTION.REMOVE_FROM_COMPARISON') : translator.trans('SHOP.COMPARATOR.ACTION.ADD_TO_COMPARISON');
  });

  const target = computed(() => {
    return router.route('web.product.show', { slug: props.product.slug });
  });

  const hasListAttributes = computed(() => {
    return props.product.listAttributes && props.product.listAttributes.length > 0;
  });

  function onRedirect(target: string) {
    gtm.push(selectItemEvent(props.product, shared.value.activeCurrencyCode));
    http.get(target);
  }

  const classes = computed(() => {
    return {
      'product--horizontal': props.horizontal,
      'product--loading': props.loading,
      'product--selected': props.selected,
    };
  });

  const promotion = computed(() => {
    if (props.product.promotion && props.product.priceGross !== null && props.product.regularGross !== null) {
      return props.product.priceGross !== props.product.regularGross;
    }

    return false;
  });

  const priceStatus = computed(() => {
    return Features.medicines.isEnabled && Features.medicalContractorsRegistry.isDisabled && props.product.isMedicinalProduct;
  });

  const alt = computed(() =>
    [
      props.product.title,
      props.product.sku,
      props.product.ean,
      props.product.brandName,
    ]
      .filter(Boolean)
      .join(' '),
  );
</script>
<template>
  <article :aria-busy="loading" :class="classes" :inert="loading" class="product" @click="onCardClick()">
    <div class="product__wrapper">
      <div class="product__image-wrapper">
        <MediaPicture :alt="alt" :photo="product.photo" sizes="490x368 2x, 245x184" type="photo" />
      </div>
      <div class="product__content">
        <div class="product__info">
          <header class="product__header">
            <component :is="header" class="product__title">
              <RouterLink :href="target">
                {{ product.title }}
              </RouterLink>
            </component>
          </header>
          <dl v-if="hasListAttributes" v-show="horizontal" class="product__details">
            <div v-for="attribute in product.listAttributes" :key="attribute.name" class="product__details-column">
              <dt class="product__property">{{ attribute.name }}:</dt>
              <dd class="product__value">{{ attribute.value }}</dd>
            </div>
          </dl>
        </div>
        <div v-if="canCustomerOrder" class="product__bottom">
          <div class="product__add-item-wrapper">
            <div class="product__price-wrapper">
              <div class="product__availability">
                <SfProductAvailability :product="product" />
              </div>
              <div class="price__catalog-price">
                <SfProductCatalogPrice :item="product" />
              </div>
              <div class="price__price">
                <span v-if="priceStatus" class="product__price-request">{{ $t('SHOP.COMMON.ACTION.ASK_FOR_PRODUCT') }}</span>
                <SfProductPrice v-else :price="product" />
              </div>
            </div>
            <SfButton
              :aria-controls="product.type === ProductType.FLEXIBLE ? null : 'product-picker-modal'"
              :aria-haspopup="product.type === ProductType.FLEXIBLE ? null : 'dialog'"
              :aria-label="product.type === ProductType.FLEXIBLE ? $t('SHOP.COMMON.ARIA.GO_TO_PRODUCT') : $t('SHOP.COMMON.ACTION.ADD_TO_CART')"
              :href="product.type === ProductType.FLEXIBLE ? target : $route('web.product.picker.create', { product: product.id, variant: product.variantId })"
              :square="!horizontal || horizontal && !breakpoints.lg"
              preserveScroll
              primary
              @click.stop="void 0"
            >
              <template v-if="horizontal && breakpoints.lg" #default>
                {{ $t('SHOP.COMMON.ACTION.ADD_TO_CART') }}
              </template>
              <template #after>
                <SfIconCartAdd aria-hidden="true" />
              </template>
            </SfButton>
          </div>
          <SfProductOmnibus :price="product" />
        </div>
        <div v-else class="product__bottom">
          <span v-if="isCustomerAccepted === undefined" class="product__bottom__guest" @click.stop="void 0">
            <RouterLink :href="$route('web.account.session.create')" rel="nofollow">
              {{ $t('SHOP.COMMON.MESSAGE.SIGN_IN_TO_SEE_PRICES_NOTICE') }}
            </RouterLink>
          </span>
          <span v-else class="product__bottom__guest">
            {{ $t('SHOP.COMMON.MESSAGE.ACCOUNT_INACTIVE_NOTICE') }}
          </span>
        </div>
      </div>
    </div>
    <div class="product__labels">
      <SfProductTag :visible="product.isNew" icon tag-type="novelty">
        {{ $t('SHOP.COMMON.LABEL.NEW') }}
      </SfProductTag>
      <SfProductTag :visible="promotion" icon tag-type="discount">
        {{ $t('SHOP.COMMON.LABEL.SALE') }}
      </SfProductTag>
      <SfProductTag :visible="product.isMedicinalProduct" icon tag-type="capsule">
        {{ $t('SHOP.COMMON.LABEL.MEDICINE') }}
      </SfProductTag>
    </div>
    <div class="product__icons">
      <slot name="icons">
        <SfWishlistButton :variant="product.variantId" :variantModel="product" class="product__wishlist" />
        <SfButton
          :aria-expanded="isComparator"
          :aria-label="compareTitle"
          :class="{ 'compared': isCompared }"
          :danger="isCompared"
          :loading="loadingComparator"
          :title="compareTitle"
          :variant="product.variantId"
          aria-controls="comparator-bar"
          class="product__compare"
          square
          tabindex="0"
          transparent
          @click.stop="toggleComparator"
        >
          <SfIconScale aria-hidden="true" />
        </SfButton>
      </slot>
    </div>
  </article>
</template>
<style lang="scss">
  :root {
    --product-card-border-color: var(--secondary-border);
    --product-card-border-color-hover: var(--primary-border);
    --product-card-border-radius: var(--border-radius);
    --product-card-background: var(--color-white);
  }
</style>
<style lang="scss" scoped>
  @use '@storefront/styles/mixins/media';

  .product {
    display: flex;
    position: relative;
    flex-grow: 1;
    border: 1px solid var(--product-card-border-color);
    background: var(--product-card-background);
    transition: var(--primary-transition);
    cursor: pointer;
    border-radius: var(--product-card-border-radius);

    &__wrapper {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      position: relative;
      padding: 1rem;
      width: 100%;

      @include media.query(lg) {
        padding: 0.625rem;
      }
    }

    &__image-wrapper {
      width: 100%;
    }

    &__content {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      flex-grow: 1;
    }

    &__header {
      margin: 1rem 0;
    }

    &__title {
      font-size: 1rem;

      a {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
        word-break: break-word;
      }
    }

    &__icons {
      position: absolute;
      right: 0.5rem;
      top: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      align-items: center;
      width: auto;
      --sf-btn-transparent-background: var(--product-card-background);
      --sf-btn-transparent-danger-background: var(--product-card-background);
    }

    &__labels {
      position: absolute;
      top: 0.5rem;
      left: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;

      @include media.query(sm) {
        flex-direction: column;
        width: auto;
      }
    }

    &__price-request {
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-sm);
      border-radius: var(--border-radius);
    }

    &__bottom {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      &__guest {
        color: var(--secondary-color);
        font-size: 0.625rem;
      }
    }

    &__add-item-wrapper {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
    }

    &__availability {
      padding-bottom: 0.25rem;
    }

    &__details {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.125rem;
      font-size: 0.75rem;

      @media (max-width: 576px) {
        column-gap: 0.625rem;
      }
    }

    &__details-column {
      display: flex;
      gap: 0.25rem;
    }

    &__value {
      display: -webkit-box;
      font-weight: var(--font-weight-semi);
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      overflow: hidden;
    }

    &__compare.compared {
      --sf-btn-foreground: var(--sf-status-color-success);
      --sf-btn-background: var(--sf-status-background-success);
      --sf-btn-foreground-hover: var(--sf-status-color-danger);
      --sf-btn-foreground-focus: var(--sf-status-color-danger);
      --sf-btn-foreground-active: var(--sf-status-color-danger);
    }

    &--horizontal {
      .product__content {
        gap: 1rem;
      }
    }

    &--loading {
      pointer-events: none;

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        height: 100%;
        width: 100%;
        background: #fff;
        border-radius: var(--product-card-border-radius);
      }

      .product__image-wrapper,
      .product__info,
      .product__bottom {
        position: relative;
        overflow: hidden;
        background: white;

        &::after {
          content: '';
          position: absolute;
          inset: 0;
          height: 90%;
          width: 100%;
          background: #eee;
          background: linear-gradient(110deg, #ececec 15%, #f5f5f5 30%, #ececec 50%);
          background-size: 200% 100%;
          animation: 1s shine linear infinite;
          border-radius: 3px;
          z-index: 1;
        }
      }
    }

    &--selected {
      border-color: var(--primary-border);
      box-shadow: 0 0 0 1px var(--primary-border);
    }

    @keyframes shine {
      to {
        background-position-x: -200%;
      }
    }

    @media(min-width: 576px) {
      &--horizontal {

        > .product__wrapper {
          flex-direction: row;
          align-items: flex-start;
          gap: 1rem;

          > .product__image-wrapper {
            width: unset;
            flex: 0 0 175px;
          }

          > .product__content {
            justify-content: space-between;
            height: 100%;

            > .product__info {
              > .product__header {
                margin-top: 0;
              }
            }
          }
        }

        > .product__icons {
          flex-direction: row;
        }
      }
    }

    @media(hover: hover) {
      &:hover {
        border-color: var(--product-card-border-color-hover)
      }
    }
  }
</style>
